import * as React from 'react';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { AdminDataWorkDTO, ApiResponse, PatchDocument, WorkApi, WorkDTO, WorkPublisherDTO } from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import FormWork from './FormWork';
import {
  Blocker,
  Location,
  NavigateFunction,
  Params,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import compare, { Patch } from '@mms-v3/patch-lib';
import { getDefaultWork } from '../../../Settings/emptyModels';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { workFormDefaultPage } from '../../../Settings/config';
import { workFormPages } from '../../../Settings/data';
import { EnrichedValues, PubtypeGroup, WorkField, WorkFormPage } from '../../../util/types';
import { Loading } from '../../system/Loading';
import { IntlShape, useIntl } from 'react-intl';
import { PubtypeUtils } from '../../../util/pubtype-utils';

export function EditWorkPage(): ReactNode {
  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const {actions} = useContext(AuthContext);
  const workApi: WorkApi = new WorkApi().withMiddleware(getAuthMiddleware(actions));
  const [blockRequested, setBlockRequested] = React.useState<boolean>(false);
  const blocker: Blocker = useBlocker(useCallback((): boolean => blockRequested, [blockRequested]));
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();

  const [work, setWork] = useState<WorkDTO>();
  const [page, setPage] = useState<WorkFormPage>(workFormDefaultPage);
  const [saving, setSaving] = useState<boolean>(false);
  const [modifiedModel, setModifiedModel] = useState<WorkDTO>();
  const [enrichedValues, setEnrichedValues] = useState<EnrichedValues>({});

  const setPubtype = (pubtype: string): void => {
    if (pubtype === modifiedModel.pubtype) return;
    setBlockRequested(true);
    if (!modifiedModel.pubtype) {
      return setModifiedModel({ ...modifiedModel, pubtype });
    }
    const oldPubtypeGroup: PubtypeGroup = PubtypeUtils.getGroupByPubtype(modifiedModel.pubtype);
    const newPubtypeGroup: PubtypeGroup = PubtypeUtils.getGroupByPubtype(pubtype);
    if (oldPubtypeGroup === newPubtypeGroup) {
      return setModifiedModel({ ...modifiedModel, pubtype });
    }
    const fields: WorkField[] = PubtypeUtils.getRemovedFields(oldPubtypeGroup, newPubtypeGroup);
    toast.confirm({
      header: intl.formatMessage({ id: 'popup.warningFieldsDeletedOnPubtype.headline' }),
      message: `${intl.formatMessage({ id: 'popup.warningFieldsDeletedOnPubtype.text' })}: ${fields
        .join(', ')
        .toLowerCase()}`,
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: intl.formatMessage({ id: 'button.confirm' }),
      rejectLabel: intl.formatMessage({ id: 'button.abort' }),
      accept: (): void => setModifiedModel((state: WorkDTO): WorkDTO => ({ ...state, pubtype })),
    });
  };

  const updateField = (field: keyof WorkDTO, value: any): void => {
    if (field === 'pubtype') {
      return setPubtype(value);
    }
    setBlockRequested(true);
    setModifiedModel((state: WorkDTO): WorkDTO => ({...state, [field]: value}));
  };

  const updateAdminField = (field: keyof AdminDataWorkDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: WorkDTO): WorkDTO => ({...state, adminDataWork: {...state.adminDataWork, [field]: value}}));
  };

  useEffect((): void => {
    if (blocker.state === 'blocked') {
      if (!blockRequested) return blocker.proceed();
      toast.confirm({
        header: intl.formatMessage({id: 'popup.unsavedInput.pageChange.headline'}),
        message: intl.formatMessage({id: 'popup.unsavedInput.pageChange.message'}),
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: intl.formatMessage({id: 'button.confirm'}),
        accept: (): void => {
          setModifiedModel(structuredClone(work));
          blocker.proceed();
        },
        rejectLabel: intl.formatMessage({id: 'button.abort'}),
        reject: (): void => blocker.reset(),
      });
    }
  }, [blocker.state]);

  useEffect((): void => {
    if (params.page !== undefined && page !== params.page.toLowerCase() && workFormPages.includes(params.page.toLowerCase())) {
      setPage(params.page.toLowerCase());
    }
    if (params.id !== undefined) {
      const id: number = Number(params.id);
      if (isNaN(id)) {
        void navigate('/error/404');
        return;
      }
      void fetchData(id);
    } else {
      setWork(location?.state?.import || getDefaultWork());
    }
  }, [location.pathname]);

  useEffect((): void => {
    setModifiedModel(structuredClone(work));
  }, [work]);

  const submit = async (edit?: {peerReviewed?: string}): Promise<void> => {
    setSaving(true);
    if(edit && edit.peerReviewed !== undefined) {
      modifiedModel.peerReviewed = edit.peerReviewed;
    }
    try {
      let response: ApiResponse<WorkDTO>;
      if (!modifiedModel.id) {
        response = await workApi.createWorkRaw({workDTO: modifiedModel});
      } else {
        const patch: Patch[] = compare(work, modifiedModel);
        response = await workApi.updateWorkRaw({
          workId: modifiedModel.id,
          patchDocument: patch as PatchDocument[],
        });
      }
      if (response.raw.ok) {
        toast.notify({severity: 'success', summary: intl.formatMessage({id: 'message.saving.success.message'})});
        const value: WorkDTO = await response.value();
        setBlockRequested(false);
        void navigate(`/work/${value.id}`);
        return;
      }
      toast.error(response, 'message.saving.failed');
    } catch (response) {
      const publisher: string = (work.workPublishers || [])
        .filter((_publisher: WorkPublisherDTO): boolean => _publisher.publisher.id == null)
        .map((_publisher: WorkPublisherDTO): string => `"${_publisher.publisher.name}"`)
        .join(', ');
      toast.error(response.response, 'message.saving.failed', {publisher});
    }
    setSaving(false);
  };

  const fetchData = async (id: number): Promise<void> => {
    const response: ApiResponse<WorkDTO> = await workApi.getWorkRaw({workId: id});
    if (!response.raw.ok) {
      toast.notify({
        severity: 'error',
        summary: intl.formatMessage({id: 'message.loading.failed.title'}),
        detail: intl.formatMessage({id: 'message.loading.failed.message'}),
      });
      return navigate(`/error/${response.raw.status}`);
    }
    setWork(await response.value());
  };
  if (!modifiedModel) {
    return React.createElement(Loading, {});
  }
  return React.createElement(FormWork, { modifiedModel, enrichedValues, submit, page, setPage, updateField, updateAdminField, saving, setModifiedModel, setEnrichedValues });
}

export default EditWorkPage;
